import React, { FC, useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { useAnalytics } from 'contexts/AnalyticsContext';
import { ANALYTICS_EVENTS } from 'constants/analyticsEvents';
import { ECardPermissionsOptions } from 'constants/graphqlTypes';
import useNavigationUserData from 'graphQL/profile/navigation/hook';
import useOpenModal from 'helpers/useOpenModal';
import Avatar from 'components/UI/Avatar';
import usePageType from 'helpers/usePageType';
import useIsModalOpen from 'helpers/useIsModalOpen';
import handleVibrate from 'helpers/handleVibrate';
import useGuideTour from 'helpers/useGuideTour';

import { TEST_TAB_BAR_HOME_BTN, TEST_TAB_BAR_PLUS_BTN, TEST_TAB_BAR_PROFILE_BTN } from 'constants/aqa/main';
import { COLORS } from 'styles/constants';
import { transformRoute } from 'helpers/routingHelper';
import { HOME_BUTTON, PICKER_BUTTON, PROFILE_BUTTON } from 'components/GuideTour/constants';
import { useGetDetailCard } from 'graphQL/card/helpers/hooks';
import { ReactComponent as PlusIcon } from 'assets/icons/Plus.svg';
import { ROUTE_ADD_CONTENT, ROUTE_ADD_PICK, ROUTE_DISCOVER, ROUTE_USER_PROFILE } from 'routes';

import withHapticFeedback from 'helpers/withHapticFeedback';
import { StyledList, StyledButton, StyledAvatarWrap } from './styled';
import { HOME_ICON_PATH } from '../../helpers/constants';

const MainMenu: FC = () => {
  const user = useNavigationUserData();
  const { track } = useAnalytics();

  const { listId } = useParams<{ listId: string }>();
  const { push } = useHistory();
  const { card } = useGetDetailCard(listId);
  const { isPickedByMe, title, cardId: listCardId, id: listPickId, isPrivateList } = card ?? {};
  const { isMyProfile, isDiscovery, isExactlyProfile, isListDetail } = usePageType();
  const { username } = user ?? {};
  const { isGuideTour } = useGuideTour();

  const isModalOpen = useIsModalOpen();
  const isHiddenByModal = isModalOpen && !isGuideTour;
  const hasEditPermission = card?.permissions?.includes(ECardPermissionsOptions.EditCollectionItems);

  // Scroll-based visibility - hide when above first card
  const [isPastFirstCard, setIsPastFirstCard] = useState(false);
  const [firstCardTop, setFirstCardTop] = useState<number | null>(null);

  const updateFirstCardPosition = useCallback(() => {
    // Look for the first card element on the page
    const targetElement =
      document.querySelector('[class*="UserCard"]') ||
      document.querySelector('[class*="CardWrap"]') ||
      document.getElementById('spotlight-container') ||
      document.querySelector('[class*="ListGrid"]') ||
      document.querySelector('[data-tour]');

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setFirstCardTop(rect.top + window.scrollY);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(updateFirstCardPosition, 500);
    window.addEventListener('resize', updateFirstCardPosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateFirstCardPosition);
    };
  }, [updateFirstCardPosition]);

  useEffect(() => {
    const handleScroll = () => {
      if (firstCardTop === null) {
        updateFirstCardPosition();
        return;
      }

      const { scrollY } = window;
      setIsPastFirstCard(scrollY >= firstCardTop);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [firstCardTop, updateFirstCardPosition]);

  const isHidden = isHiddenByModal || !isPastFirstCard;

  const openAddContent = useOpenModal(
    isPickedByMe && hasEditPermission ? ROUTE_ADD_PICK : ROUTE_ADD_CONTENT,
    false,
    isPickedByMe && hasEditPermission
      ? {
          listCardId,
          listPickId,
          listTitle: title,
          isPrivateList,
        }
      : undefined
  );
  const handleAddContent = () => {
    if (isListDetail) {
      track(ANALYTICS_EVENTS.CreatePickFromList);
    }

    handleVibrate();
    openAddContent();
  };

  const scrollToTop = () =>
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

  return username ? (
    <StyledList isHidden={isHidden}>
      <li>
        <StyledButton
          data-testid={TEST_TAB_BAR_HOME_BTN}
          data-tour={HOME_BUTTON}
          onClick={withHapticFeedback(() => (isDiscovery ? scrollToTop() : push(ROUTE_DISCOVER)))}
        >
          <svg width="24" height="24">
            <path
              d={HOME_ICON_PATH}
              fill={isDiscovery ? COLORS.white[100] : 'none'}
              stroke={COLORS.white[100]}
              strokeWidth="1.5"
            />
          </svg>
        </StyledButton>
      </li>
      <li>
        <StyledButton data-testid={TEST_TAB_BAR_PLUS_BTN} data-tour={PICKER_BUTTON} onClick={handleAddContent}>
          <PlusIcon viewBox="0 0 24 24" height="44px" width="44px" fill={COLORS.white[100]} />
        </StyledButton>
      </li>
      <li>
        <StyledButton
          data-testid={TEST_TAB_BAR_PROFILE_BTN}
          data-tour={PROFILE_BUTTON}
          onClick={withHapticFeedback(() =>
            isExactlyProfile && isMyProfile
              ? scrollToTop()
              : push(transformRoute(ROUTE_USER_PROFILE, { profileName: username }))
          )}
        >
          <StyledAvatarWrap hasBorder={isMyProfile}>{user && <Avatar user={user} dimension={24} />}</StyledAvatarWrap>
        </StyledButton>
      </li>
    </StyledList>
  ) : null;
};

export default MainMenu;
